import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Stepper, type StepsModel } from '@/components/ui/stepper';
import { Typography } from '@/components/ui/typography';

import {
  PaymentFailedProgressLoader,
  PaymentProgressLoader,
} from '../payment/PaymentProgressLoader';

import { BulkImportPage } from '../../properties/bulk-import/BulkImportPage';
import { FixErrorImports } from '../../properties/bulk-import/fix-errors/FixErrorsPage';
import PaymentPage from '../payment/PaymentPage';

import { useBulkImportOrder } from './hooks/useBulkImportOrder';

const STEPS: StepsModel[] = [
  { title: 'Upload Template', id: 1 },
  { title: 'Review', id: 2 },
  { title: 'Payment', id: 3 },
];

const BulkImportOrderPage = () => {
  const {
    activeStep,
    setActiveStep,
    setSkippedActiveStep,
    setSkippedStepFromStepper,
    fileId,
    setFileId,
    uploadedByInfo,
    setUploadedByInfo,
    fileName,
    setFileName,
    dateInfo,
    setDateInfo,
    fileState,
    setFileState,
    fileNumber,
    setFileNumber,
    fileUploadDisabled,
    setFileUploadDisabled,
    tableData,
    setTableData,
    successData,
    setSuccessData,
    failedData,
    setFailedData,
    fixErrorTableData,
    setFixErrorTableData,
    fromTableDataFromNextPage,
    setFromTableDataFromNextPage,
    orderCountInfo,
    setOrderCountInfo,
    amount,
    setAmount,
    showStep3,
    setShowStep3,
    isContinue,
    setIsContinue,
    setPaymentInfo,
    setPromoCodeFromPaymentBulkOrder,
    setSavingsAmountFromPayment,
    callSubmitOrder,
    setCallSubmitOrders,
    paymentLoader,
    setPaymentLoader,
    paymentFailedLoader,
    setPaymentFailedLoader,
    onCancel,
  } = useBulkImportOrder();

  function renderStep() {
    switch (activeStep) {
      case 1:
        return (
          <BulkImportPage
            isProperty={false}
            setFileId={setFileId}
            setUploadedByInfo={setUploadedByInfo}
            setFileName={setFileName}
            setDateInfo={setDateInfo}
            setTableData={setTableData}
            setAmount={setAmount}
            setOrderCountInfo={setOrderCountInfo}
            setSuccessData={setSuccessData}
            setFailedData={setFailedData}
            setActiveStep={setActiveStep}
            setSkippedActiveStep={setSkippedActiveStep}
            showStep3={showStep3}
            setShowStep3={setShowStep3}
            setFromTableDataFromNextPage={setFromTableDataFromNextPage}
            setSkippedStepFromStepper={setSkippedStepFromStepper}
            setFileNumber={setFileNumber}
            fileNumber={fileNumber}
            fromTableDataFromNextPage={fromTableDataFromNextPage}
            setFixErrorTableData={setFixErrorTableData}
            setFileState={setFileState}
            isContinue={isContinue}
            setIsContinue={setIsContinue}
            fileId={fileId}
            uploadedByInfo={uploadedByInfo}
            fileName={fileName}
            tableData={tableData}
            amount={amount}
            orderCountInfo={orderCountInfo}
            successData={successData}
            failedData={failedData}
            setFileUploadDisabled={setFileUploadDisabled}
            fileUploadDisabled={fileUploadDisabled}
          />
        );
      case 2:
        return (
          <FixErrorImports
            tableData={tableData ?? undefined}
            successData={successData ?? undefined}
            failedData={failedData ?? undefined}
            dateInfo={dateInfo}
            uploadedByInfo={uploadedByInfo}
            fileId={fileId ?? undefined}
            fileState={fileState ?? undefined}
            fromTableDataFromNextPage={fromTableDataFromNextPage}
            setFileId={setFileId}
            setAmount={setAmount}
            setActiveStep={setActiveStep}
            setOrderCountInfo={setOrderCountInfo}
            setFailedData={setFailedData}
            setSuccessData={setSuccessData}
            setFixErrorTableData={setFixErrorTableData}
            fixErrorTableData={fixErrorTableData}
            setSkippedStepFromStepper={setSkippedStepFromStepper}
          />
        );
      case 3:
        return (
          <PaymentPage
            setActiveStep={setActiveStep}
            setPaymentInfo={setPaymentInfo}
            setSkippedStepFromStepper={setSkippedStepFromStepper}
            setPromoCodeFromPaymentBulkOrder={setPromoCodeFromPaymentBulkOrder}
            setSavingsAmountFromPayment={setSavingsAmountFromPayment}
            setCallSubmitOrders={setCallSubmitOrders}
            setFailedData={setFailedData}
            setTableData={setTableData}
            setSuccessData={setSuccessData}
            setFileState={setFileState}
            setFixErrorTableData={setFixErrorTableData}
            tableData={tableData}
            finalPaymentAmount={setAmount}
            orderTotalAmount={amount}
            isBulkOrder={true}
            failedCountInfo={failedData}
            fileIdInfo={fileId}
            fileNameInfo={fileName}
            successData={successData}
            orderCount={orderCountInfo}
            paymentLoader={paymentLoader}
          />
        );
      default:
        return null;
    }
  }

  return (
    // Normal-flow root: h-full fills <main>'s box. Step 3's PaymentPage still
    // roots `absolute inset-0`, now resolving against <main>'s `relative` —
    // the same box this section occupies, so the payment step's full-page
    // takeover is unchanged (its own conversion belongs to Unit 4.5).
    <section className="flex h-full flex-col bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-card px-6 py-4">
        <Button variant="ghost" size="icon" type="button" onClick={onCancel} tooltip="Back to new orders">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Typography as="h1" variant="heading-lg" className="truncate font-medium">
          Place Bulk Order
        </Typography>
      </header>

      {/* Deliberate horizontal scroller below lg: three labeled steps exceed a
          phone viewport, so the strip scrolls rather than wrapping or shrinking
          (spec §2.7). The testid anchors the e2e overflow allowlist. */}
      <div
        className="h-12 shrink-0 overflow-x-auto border-b border-border bg-card"
        data-testid="bulk-import-stepper"
      >
        <Stepper
          steps={STEPS}
          activeStep={activeStep}
          iconCheck={true}
          updateActiveStep={setActiveStep}
          canClick={false}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">{renderStep()}</div>

      {activeStep === 1 && (
        <footer className="flex shrink-0 items-center justify-between border-t border-border bg-card px-6 py-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Back
          </Button>
          <Button type="button" disabled={isContinue} onClick={() => setActiveStep(2)}>
            Next
          </Button>
        </footer>
      )}

      {paymentLoader && (
        <Modal
          open={paymentLoader}
          onOpenChange={() => setPaymentLoader(true)}
          className="RPTable"
          size="sm"
          srOnlyTitle="Order request in progress"
        >
          <PaymentProgressLoader />
        </Modal>
      )}

      {paymentFailedLoader && (
        <Modal
          open={paymentFailedLoader}
          onOpenChange={setPaymentFailedLoader}
          className="RPTable"
          size="sm"
          srOnlyTitle="Order request failed"
          footer={
            <div className="flex justify-end p-2">
              <Button variant="outline" onClick={() => setPaymentFailedLoader(false)}>
                Select Different Card
              </Button>
            </div>
          }
        >
          <PaymentFailedProgressLoader />
        </Modal>
      )}
    </section>
  );
};

export default BulkImportOrderPage;
